import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow } from "./bits";
import { ChaosCanvas } from "./ChaosCanvas";

const STATEMENT = "Financial decisions shouldn't feel complicated.";

const Word = ({ progress, range, children, accent }) => {
  const opacity = useTransform(progress, range, [0.08, 1]);
  return (
    <motion.span
      style={{ opacity }}
      className={`mr-[0.26em] inline-block ${accent ? "font-display italic text-emerald-600" : ""}`}
    >
      {children}
    </motion.span>
  );
};

export const Problem = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const order = useTransform(scrollYProgress, [0.1, 0.85], [0, 1]);
  const subOpacity = useTransform(scrollYProgress, [0.3, 0.42], [0, 1]);
  const chaosOpacity = useTransform(order, [0, 0.5], [1, 0]);
  const clarityOpacity = useTransform(order, [0.55, 1], [0, 1]);
  const words = STATEMENT.split(" ");

  return (
    <section id="problem" data-testid="problem-section" ref={ref} className="relative h-[260vh]">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-6">
          <Eyebrow>01 — The Problem</Eyebrow>
          <p
            data-testid="problem-statement"
            className="mt-8 max-w-4xl text-4xl font-medium leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {words.map((word, i) => (
              <Word
                key={`${word}-${i}`}
                progress={scrollYProgress}
                range={[0.04 + (i / words.length) * 0.24, 0.04 + ((i + 1) / words.length) * 0.24]}
                accent={i === words.length - 1}
              >
                {word}
              </Word>
            ))}
          </p>
          <motion.p
            style={{ opacity: subOpacity }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#4E6A82] sm:text-lg"
          >
            Markets change. Goals evolve. Life rarely follows a spreadsheet. Your financial strategy
            shouldn't be built around complexity — it should be built around you.
          </motion.p>
        </div>
        <div className="relative mt-10 h-[36vh] w-full">
          <ChaosCanvas progress={order} />
          <motion.span
            style={{ opacity: chaosOpacity }}
            data-testid="chaos-label"
            className="absolute left-6 top-2 font-mono text-[10px] uppercase tracking-[0.4em] text-amber-600/80"
          >
            Chaos
          </motion.span>
          <motion.span
            style={{ opacity: clarityOpacity }}
            data-testid="clarity-label"
            className="absolute bottom-2 right-6 font-mono text-[10px] uppercase tracking-[0.4em] text-emerald-600/80"
          >
            Clarity
          </motion.span>
        </div>
      </div>
    </section>
  );
};
